import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ApplicationCard} from '../../components/common/applications/applicationCard';
import {ApplicationCardImageAvatar} from '../../components/common/applications/applicationCardImageAvatar';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {IntegrationTypeModel} from '../../models/api/integrationTypesModel';
import {buildSettingsAppsAppDetailsUrl, buildSettingsAppsTabUrl} from '../../models/url/settingsUrlHelpers';
import {SettingsAppsUrl} from '../../models/url/settingsUrlModel';

/*
 * Props.
 */

interface SettingsAppCardProps {
  integrationType: IntegrationTypeModel;
  companyUrl: string;
  url: SettingsAppsUrl;
  isIntegrationEnabled: boolean;
  isIntegrationAvailableOnPlan: boolean;
  shouldHideUnavailableIntegration?: boolean;
}

/*
 * Style.
 */

const StyledApplicationCard = styled(ApplicationCard)`
  min-width: 160px;
`;

/*
 * Component.
 */

export const SettingsAppCard: FC<SettingsAppCardProps> = (props) => {
  const {
    integrationType,
    companyUrl,
    url,
    isIntegrationEnabled,
    isIntegrationAvailableOnPlan,
    shouldHideUnavailableIntegration,
  } = props;
  const {navigateToLink} = useLinkNavigation();

  const onButtonClick = () => {
    navigateToLink(
      buildSettingsAppsAppDetailsUrl(
        integrationType.id,
        buildSettingsAppsTabUrl(url.tabPage, url.searchQuery),
      ),
    );
  };

  // If the integration is not available and we want to hide unavailable integrations.
  if (!isIntegrationAvailableOnPlan && shouldHideUnavailableIntegration) {
    return null;
  }

  return (
    <StyledApplicationCard
      key={integrationType.id}
      name={integrationType.name}
      description={integrationType.description}
      isEnabled={isIntegrationEnabled}
      isUpsellRequired={!isIntegrationAvailableOnPlan}
      avatar={
        <ApplicationCardImageAvatar
          url={`${companyUrl}/integration_types/${integrationType.id}/public/icons/`}
        />
      }
    >
      <Button onClick={onButtonClick}>
        <Trans id="settings-apps-card-button">See details</Trans>
      </Button>
    </StyledApplicationCard>
  );
};
